import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableSortLabel } from "@mui/material";
import OverviewTablePagination from "src/scenes/overview/overview-components/details-tables/overview-table-pagination";
import { format } from "date-fns";
import startCase from "lodash-es/startCase";
import TimeSplitter from "../utils/time-splitter";
import ExternalLink from "./external-link";

const TicketsStateDurationTable = ({ data, hiddenSeries, formats }) => {
    const [columns, setColumns] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [sortOrder, setSortOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("");

    React.useEffect(() => {
        if (data && data[0] && Object.keys(data[0])) {
            setColumns(
                Object.keys(data[0]).map((k) => ({
                    id: k,
                    label: k.endsWith(")")
                        ? `${startCase(k.slice(0, k.indexOf("(")))} ${k.slice(k.indexOf("("))}`
                        : startCase(k),
                    minWidth: 130,
                    isSortable: true,
                }))
            );
        }
    }, [data && JSON.stringify(data)]);

    if (!data) return null;

    const rows = data.filter((d) => !hiddenSeries.includes(d.state));

    const handleRequestSort = (_event, property) => {
        const isAsc = orderBy === property && sortOrder === "asc";
        setSortOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const orderResult = comparator(a[0], b[0]);
            if (orderResult !== 0) return orderResult;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const descendingComparator = (a, b) => {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    };

    const getComparator = () => {
        return sortOrder === "desc"
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const sortedRows = stableSort(rows, getComparator(sortOrder, orderBy));

    const needsPagination = rowsPerPage < rows.length;

    return (
        <Paper sx={{ width: "100%", overflow: "hidden", marginTop: "8px", boxShadow: "none" }}>
            <TableContainer>
                <Table stickyHeader size="small" aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    style={{ minWidth: column.minWidth, fontSize: "12px" }}
                                >
                                    {column.isSortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? sortOrder : "asc"}
                                            onClick={(event) => handleRequestSort(event, column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                        {columns.map((column) => {
                                            let value = row[column.id];
                                            if (
                                                formats &&
                                                formats[column.id] &&
                                                formats[column.id] === "workSeconds"
                                            ) {
                                                value = TimeSplitter(value, "work-seconds");
                                            } else {
                                                if (
                                                    (column.id === "from" ||
                                                        column.id === "until") &&
                                                    value
                                                ) {
                                                    value = format(
                                                        new Date(value),
                                                        "dd/MM/yyyy HH:mm:ss"
                                                    );
                                                }
                                                if (
                                                    (column.id === "duration" ||
                                                        column.id === "overallDuration") &&
                                                    value
                                                ) {
                                                    value = `${TimeSplitter(
                                                        value,
                                                        "variable-accuracy"
                                                    )} ${TimeSplitter(value, "text")}`;
                                                }
                                                if (column.id === "url" && value) {
                                                    const splitValue = value.split("/");
                                                    value = (
                                                        <ExternalLink href={value}>
                                                            {splitValue[splitValue.length - 1]}
                                                        </ExternalLink>
                                                    );
                                                }
                                            }
                                            return (
                                                <TableCell
                                                    key={column.id}
                                                    align={column.align}
                                                    style={{
                                                        fontSize: "12px",
                                                        backgroundColor: "#333333",
                                                    }}
                                                >
                                                    {column.format && typeof value === "number"
                                                        ? column.format(value)
                                                        : value}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            {needsPagination &&
                OverviewTablePagination({ rows, rowsPerPage, page, setPage, setRowsPerPage })}
        </Paper>
    );
};

export default TicketsStateDurationTable;
